import { useCallback, useEffect, useState } from "react";
import { Map, Loader2, RefreshCw, AlertTriangle, ChevronRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import "./CommunityIntelligence.css";

const SEVERITY = {
  severe: { color: "#7F1D1D", bg: "#FECACA", label: "Severe" },
  desert: { color: "#9A3412", bg: "#FED7AA", label: "Desert" },
  watch: { color: "#92400E", bg: "#FEF3C7", label: "Watch" },
  ok: { color: "#166534", bg: "#DCFCE7", label: "OK" },
};

function heatColor(score) {
  // 0-100 mapped to green→amber→red
  if (score >= 80) return "#DC2626";
  if (score >= 60) return "#EA580C";
  if (score >= 40) return "#D97706";
  if (score >= 20) return "#65A30D";
  return "#16A34A";
}

export default function ResourceDesertDetector() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serviceType, setServiceType] = useState("");
  const [selected, setSelected] = useState(null);
  const [recs, setRecs] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const url = serviceType
        ? `/analytics/resource-desert?service_type=${encodeURIComponent(serviceType)}`
        : "/analytics/resource-desert";
      const { data } = await api.get(url);
      setData(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [serviceType]);
  useEffect(() => { load(); }, [load]);

  const openHotspot = async (h) => {
    setSelected(h); setRecs(null);
    try {
      const url = `/analytics/resource-desert/${encodeURIComponent(h.key)}/recommendations`
        + (serviceType ? `?service_type=${encodeURIComponent(serviceType)}` : "");
      const { data } = await api.get(url);
      setRecs(data.recommendations);
    } catch (e) { setRecs([formatApiError(e)]); }
  };

  const allCategories = data
    ? Array.from(new Set(
        data.hotspots.flatMap((h) => [
          ...h.categories_referred, ...h.categories_served,
        ]),
      )).sort()
    : [];

  return (
    <div className="hw-ci" data-testid="resource-desert">
      <div className="hw-ci__eyebrow">
        <Map className="w-3.5 h-3.5"
              style={{ display: "inline", verticalAlign: "text-bottom",
                        marginRight: "0.3rem" }} />
        Community Intelligence · Resource Desert Detector
      </div>
      <h1 className="hw-ci__title">Where is unmet need concentrated?</h1>
      <p className="hw-ci__sub">
        Composite score combining demand, provider gap, missing service categories,
        wait times, decline rate, and population concentration.
        Counties scoring <strong>60+</strong> are flagged as deserts.
      </p>

      <div className="hw-ci__controls">
        <select className="hw-ci__select" value={serviceType}
                 onChange={(e) => setServiceType(e.target.value)}
                 data-testid="rd-service-type">
          <option value="">All service types</option>
          {allCategories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <button className="hw-ci__btn" onClick={load} data-testid="rd-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      {error && <div style={{ color: "#B91C1C" }}>{error}</div>}
      {loading && !data && (
        <div style={{ color: "#0F766E" }}>
          <Loader2 className="w-4 h-4 animate-spin" style={{ display: "inline" }} /> loading…
        </div>
      )}

      {data && (
        <>
          <div className="hw-ci__kpi-grid">
            <Kpi label="Counties analyzed" value={data.summary.total_keys} testid="rd-summary-total" />
            <Kpi label="Severe deserts" value={data.summary.severe} testid="rd-summary-severe" />
            <Kpi label="Deserts" value={data.summary.deserts - data.summary.severe} testid="rd-summary-desert" />
            <Kpi label="Watch" value={data.summary.watch} testid="rd-summary-watch" />
            <Kpi label="Healthy" value={data.summary.ok} testid="rd-summary-ok" />
          </div>

          <div className="hw-ci__panel" data-testid="rd-heatmap">
            <h2>
              <span>Heatmap — score by county</span>
              <small>Green (healthy) → red (severe desert)</small>
            </h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
              {data.hotspots.map((h) => (
                <div key={h.key}
                      className="hw-ci__heat-cell"
                      style={{ background: heatColor(h.score), cursor: "pointer" }}
                      onClick={() => openHotspot(h)}
                      data-testid={`rd-heat-${h.key}`}
                      title={`Score ${h.score} · ${h.referrals} referrals · ${h.active_resources} resources`}>
                  {h.key} · {h.score}
                </div>
              ))}
              {data.hotspots.length === 0 && (
                <div style={{ color: "#64748B", fontSize: "0.85rem" }}>
                  No hotspots computed yet — need referrals + resources data.
                </div>
              )}
            </div>
          </div>

          <div className="hw-ci__panel" data-testid="rd-hotspots">
            <h2>
              <span>Hotspots (click for recommendations)</span>
              <small>Sorted by desert score</small>
            </h2>
            <table className="hw-ci__table">
              <thead>
                <tr>
                  <th>County</th>
                  <th>Score</th>
                  <th>Severity</th>
                  <th>Referrals</th>
                  <th>Declined</th>
                  <th>Resources</th>
                  <th>Care seekers</th>
                  <th>Missing categories</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.hotspots.map((h) => (
                  <tr key={h.key} data-testid={`rd-row-${h.key}`}
                       style={{ cursor: "pointer" }}
                       onClick={() => openHotspot(h)}>
                    <td><strong>{h.key}</strong></td>
                    <td>
                      <div className="hw-ci__score-bar">
                        <span style={{ fontWeight: 700, minWidth: 32 }}>{h.score}</span>
                        <div className="hw-ci__score-bar-track">
                          <div className="hw-ci__score-bar-fill"
                                style={{ width: `${h.score}%`, background: heatColor(h.score) }} />
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`hw-ci__pill hw-ci__pill--${h.severity}`}>
                        {SEVERITY[h.severity]?.label || h.severity}
                      </span>
                    </td>
                    <td>{h.referrals}</td>
                    <td>{h.declined} ({h.decline_rate_pct}%)</td>
                    <td>{h.active_resources}</td>
                    <td>{h.care_seekers}</td>
                    <td style={{ fontSize: "0.75rem", color: "#64748B" }}>
                      {h.categories_missing.slice(0, 3).join(", ") || "—"}
                    </td>
                    <td><ChevronRight className="w-3.5 h-3.5" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {selected && (
            <div className="hw-ci__panel" data-testid="rd-detail-panel"
                  style={{ borderColor: heatColor(selected.score),
                            borderWidth: "2px" }}>
              <h2>
                <span>
                  <AlertTriangle className="w-4 h-4"
                                    style={{ display: "inline", verticalAlign: "text-bottom",
                                              marginRight: "0.3rem",
                                              color: heatColor(selected.score) }} />
                  {selected.key} — score {selected.score} ·
                  {" "}<span className={`hw-ci__pill hw-ci__pill--${selected.severity}`}>
                    {SEVERITY[selected.severity]?.label}
                  </span>
                </span>
                <button className="hw-ci__btn"
                         onClick={() => setSelected(null)}>Close</button>
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
                <div>
                  <h3 style={{ fontSize: "0.85rem", color: "#0F172A",
                                 margin: "0 0 0.4rem" }}>Score contributions</h3>
                  <table className="hw-ci__table" data-testid="rd-detail-contrib">
                    <tbody>
                      {Object.entries(selected.contributions).map(([k, v]) => (
                        <tr key={k}>
                          <td style={{ textTransform: "capitalize" }}>
                            {k.replace(/_/g, " ")}
                          </td>
                          <td style={{ textAlign: "right", fontWeight: 700 }}>+{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div>
                  <h3 style={{ fontSize: "0.85rem", color: "#0F172A",
                                 margin: "0 0 0.4rem" }}>Recommendations</h3>
                  {!recs ? (
                    <div style={{ color: "#0F766E" }}>
                      <Loader2 className="w-3.5 h-3.5 animate-spin"
                                style={{ display: "inline" }} /> generating…
                    </div>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: "1.1rem",
                                    color: "#0F172A", fontSize: "0.85rem", lineHeight: 1.6 }}
                        data-testid="rd-detail-recs">
                      {recs.map((r, idx) => <li key={idx}>{r}</li>)}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div className="hw-ci__kpi" data-testid={testid}>
      <div className="hw-ci__kpi-label">{label}</div>
      <div className="hw-ci__kpi-value">
        {typeof value === "number" ? new Intl.NumberFormat().format(value) : value}
      </div>
    </div>
  );
}
